import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../hooks/useAuth';

interface UserMenuProps {
  onOpenAuth: (mode: 'login') => void;
  onOpenProfile?: () => void;
}

export const UserMenu: React.FC<UserMenuProps> = ({ onOpenAuth, onOpenProfile }) => {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  if (!user) {
    return (
      <AuthButtons>
        <SignInBtn onClick={() => onOpenAuth('login')}>Sign In</SignInBtn>
      </AuthButtons>
    );
  }

  const displayName = user.first_name || user.email.split('@')[0];
  const initials = (user.first_name?.[0] || '') + (user.last_name?.[0] || '') || user.email[0].toUpperCase();

  return (
    <Container ref={ref}>
      <AvatarBtn onClick={() => setOpen(!open)}>
        <Avatar>{initials}</Avatar>
        <UserName>{displayName}</UserName>
        <Caret>&#9662;</Caret>
      </AvatarBtn>
      {open && (
        <Dropdown>
          <UserInfo>
            <strong>{user.first_name} {user.last_name}</strong>
            <Email>{user.email}</Email>
            {user.profile?.company && <Company>{user.profile.company}</Company>}
          </UserInfo>
          <Divider />
          {onOpenProfile && (
            <MenuItem onClick={() => { setOpen(false); onOpenProfile(); }}>Profile Settings</MenuItem>
          )}
          {user.is_staff && (
            <MenuItem as="a" href="/panel/">Admin Panel</MenuItem>
          )}
          <Divider />
          <MenuItem onClick={() => { setOpen(false); logout(); }}>Sign Out</MenuItem>
        </Dropdown>
      )}
    </Container>
  );
};

const AuthButtons = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const SignInBtn = styled.button`
  padding: 5px 14px;
  background: transparent;
  color: white;
  border: 1px solid rgba(255,255,255,0.5);
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  &:hover { background: rgba(255,255,255,0.1); border-color: white; }
`;

const Container = styled.div`
  position: relative;
`;

const AvatarBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  color: white;
  &:hover { background: rgba(255,255,255,0.1); }
`;

const Avatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(255,255,255,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 600;
  color: white;
`;

const UserName = styled.span`
  font-size: 13px;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Caret = styled.span`
  font-size: 10px;
  opacity: 0.6;
`;

const Dropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 6px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  min-width: 200px;
  z-index: 10000;
  overflow: hidden;
`;

const UserInfo = styled.div`
  padding: 12px 16px;
  font-size: 13px;
  color: #333;
`;

const Email = styled.div`
  font-size: 12px;
  color: #888;
  margin-top: 2px;
`;

const Company = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 2px;
`;

const Divider = styled.div`
  height: 1px;
  background: #eee;
`;

const MenuItem = styled.button`
  display: block;
  width: 100%;
  padding: 10px 16px;
  background: none;
  border: none;
  text-align: left;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  text-decoration: none;
  &:hover { background: #f5f5f5; }
`;
